/* Mobile filter drawer — the scrim, the scroll lock and the keyboard behaviour for
   any `.sb` browse panel (stone species pages, diamonds, made-to-order jewelry).
   Owner rule 2026-08-06: one filter behaviour on the whole store, and the thing that
   slides in is the VISUAL panel, not a list of checkboxes.

   ── THIS FILE IS AN ENHANCEMENT, NOT THE MECHANISM ───────────────────────────
   Open/closed lives in `.sb__cb`, a hidden checkbox, and `filters-drawer.css` does
   the sweep. So with this script blocked, failing to parse, or simply slow, the
   drawer STILL OPENS AND CLOSES — it just has no dimmed backdrop, no scroll lock and
   no Escape key. Everything below is written to degrade to that, and nothing here
   may become load-bearing for opening the panel. Same contract `gem-range` follows
   in stone-browse.js.

   ⚠️ DO NOT "SIMPLIFY" THIS BY BINDING A CLICK HANDLER TO THE TOGGLE AND SETTING
   `.is-open` YOURSELF. That is the version that breaks with JS off, and the panel it
   hides is the only filter UI on these templates — the theme's own facet rail is
   switched off (`enable_filtering: false`), so a shopper would have no way to filter
   at all. */
(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';
  /* 🚨 THE DRAWER IS NOT ALWAYS A MOBILE THING ANY MORE (owner 2026-08-21). A JEWELRY
     panel (`.jb`) is a Filter & browse button plus a slide-in drawer at EVERY width;
     species, diamond and builder panels are unchanged and still switch to an inline
     rail at 750px. So the media query is chosen PER PANEL — a module-level constant
     would have made this an all-or-nothing change to every browse surface in the
     store. `'all'` matches at any width, so the jewelry path never consults a
     breakpoint at all.
     The CSS half is generated by build_jewelry_desktop_drawer.py; this file only
     supplies the scrim state, the scroll lock and Escape, and remains an
     ENHANCEMENT — with it blocked, the checkbox still opens the panel. */
  var ALWAYS = 'all';

  function init(panel) {
    var q = panel.classList.contains('jb') ? ALWAYS : MOBILE;
    var cb = panel.querySelector('.sb__cb');
    var body = panel.querySelector('.sb__body');
    /* ⚠️ THE LABEL IS NOT ALWAYS INSIDE THE PANEL (2026-09-14). On a phone a jewelry
       panel's label is moved into the product grid's sort row by sr-filter-dock.liquid,
       before this runs. Found by its `for`, it still gets focus back on close. */
    var toggle = panel.querySelector('.sb__toggle') ||
      (cb && cb.id ? document.querySelector('label.sb__toggle[for="' + cb.id + '"]') : null);
    if (!cb || !body || cb.dataset.drawerReady) return;
    cb.dataset.drawerReady = '1';

    /* ---- Enter opens and closes it too (audit M152, 2026-09-17) ------------ */
    /* A checkbox answers only Space, and a keyboard shopper expects Enter to work on
       a control that looks like a button. `click()` toggles the box and fires
       `change`, so sync() runs exactly as it does for Space, and with this script
       missing Space still works on its own. ⚠️ ONLY WHILE ITS LABEL IS ON SCREEN:
       from 750px up a loose-stone panel hides its label and shows the rail inline, and
       the checkbox does nothing there (as with Space). The label is found by `for`, so
       a jewelry label docked in the sort row on a phone counts. */
    cb.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.repeat || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.isComposing) return;
      if (!toggle || !toggle.getClientRects().length) return;
      e.preventDefault();
      cb.click();
    });

    /* ---- a tap or click on the label must not move the page (audit M152, 2026-09-17) */
    /* 🚨 A FOCUSABLE CHECKBOX MOVES THE PAGE WHEN ITS LABEL IS TAPPED. Chrome answers a
       label click by focusing its checkbox, and focusing scrolls it into view. The jewelry
       checkbox sits at the top of the panel while a phone shows its label down in the
       grid's sort row, so with the label half under the top edge the page jumped to the
       checkbox (measured in headless Chrome: 45px to 0 at 390, 44px to 0 at 1440;
       jewelry-keyboard/offline_focus_check.mjs). Before the checkbox lost `hidden` it could
       not take focus and nothing moved. So the label's own action is replaced by its toggle
       alone: no focus, so no scroll, and no keyboard ring drawn after a pointer (a focus()
       from here did draw it). A keyboard never clicks the label; it has the checkbox.
       Delegated, because the label moves (sr-filter-dock) and gem and diamond search redraw
       their panel. With this script missing the label works as before. */
    document.addEventListener('click', function (e) {
      var label = e.target.closest && e.target.closest('label.sb__toggle');
      if (!label || e.defaultPrevented || !cb.id || label.htmlFor !== cb.id) return;
      e.preventDefault();
      cb.click();
    });

    /* ---- scrim ------------------------------------------------------------- */
    /* A <button>, not a <div>: it is a real close control, so it belongs in the tab
       order and needs an accessible name. Inserted after the body so it cannot end up
       painted over the drawer regardless of stacking context. */
    var scrim = document.createElement('button');
    scrim.type = 'button';
    scrim.className = 'sb-scrim';
    scrim.setAttribute('aria-label', 'Close filters');
    scrim.tabIndex = -1;
    body.insertAdjacentElement('afterend', scrim);

    /* ---- in-drawer header, with the close button --------------------------- */
    var head = document.createElement('div');
    head.className = 'sb__drawer-head';
    head.innerHTML =
      '<span class="sb__drawer-title">Filter &amp; browse</span>' +
      '<button type="button" class="sb__drawer-x" aria-label="Close filters">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
      'aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg></button>';
    body.insertAdjacentElement('afterbegin', head);
    /* ⚠️ A PANEL THAT IS DRAWN IN THE BROWSER WIPES IT (2026-09-14). diamond-search and
       stone-search empty `[data-db-panel]` and rebuild it on every change, which took the
       title and close button with it. Put the same element back on top after each redraw
       (observer callbacks run once the redraw has finished). A static panel never fires. */
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (body.firstElementChild !== head) body.insertAdjacentElement('afterbegin', head);
      }).observe(body, { childList: true });
    }

    function close() {
      cb.checked = false;
      sync();
      /* FOCUS GOES BACK TO THE CHECKBOX, WITHOUT SCROLLING (audit M152, 2026-09-17). It
         is the control a keyboard reaches, and on a jewelry phone it sits above the grid
         while its label is docked in the sort row, so a plain focus() could move the
         page. Focusing the label only worked because Chrome hands a label's focus to its
         checkbox, and it scrolled when it did. */
      if (matchMedia(q).matches) cb.focus({ preventScroll: true });
    }

    scrim.addEventListener('click', close);
    head.querySelector('.sb__drawer-x').addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && cb.checked && matchMedia(q).matches) close();
    });

    function sync() {
      var open = cb.checked && matchMedia(q).matches;
      scrim.classList.toggle('is-open', open);
      scrim.tabIndex = open ? 0 : -1;
      /* ⚠️ LOCK THE BODY, NOT THE HTML ELEMENT, AND ONLY ON MOBILE. `overflow:hidden`
         on <html> kills smooth scrolling on iOS for the rest of the session; on
         <body> it is released cleanly. Guarded on the media query so a desktop
         resize while "checked" cannot leave the page unscrollable — which is exactly
         what happens if you sync on the checkbox alone. */
      document.body.style.overflow = open ? 'hidden' : '';
    }

    cb.addEventListener('change', sync);

    /* A resize across the breakpoint must not strand the page: if the drawer was open
       on a phone and the viewport becomes a desktop, release the scroll lock. */
    var mq = matchMedia(q);
    (mq.addEventListener ? mq.addEventListener.bind(mq, 'change')
                         : mq.addListener.bind(mq))(sync);

    sync();
  }

  /* ON A PHONE THE GRID / LIST PAIR SHOWS ONE BUTTON: the layout you are NOT on
     (filters-drawer.css, 2026-09-14), so pressing it hides the button that has focus.
     Hand focus to the one that replaced it rather than dropping it on <body>.
     stone-list.js does the switching; this only follows it. */
  function followViewFocus() {
    document.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('.sb-view');
      if (!b || document.activeElement !== b) return;
      requestAnimationFrame(function () {
        if (b.getClientRects().length) return;
        var group = b.closest('.sb__views');
        var next = group && Array.prototype.find.call(group.querySelectorAll('.sb-view'), function (o) {
          return o !== b && o.getClientRects().length;
        });
        if (next) next.focus();
      });
    });
  }

  function boot() {
    document.querySelectorAll('.sb').forEach(init);
    followViewFocus();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
